import React, { useEffect, useState } from 'react';
import { InvoiceWithLineItems, ServiceCreditRequest } from '@atlassian/cssxp-types-billing';
import { DatePicker as AkDatePicker } from '@atlaskit/datetime-picker';
import {
  CreditableProduct,
  FormValidationErrors,
  getCreditableProducts,
  validateCreditAmount,
  isSupportTicket,
  isFutureDate,
} from '@atlassian/cssxp-utils-invoice';
import {
  addNotification,
  FormattedCurrency,
  InfoPanel,
  RequiredFieldMessage,
  successNotificationBuilder,
  useSidebar,
} from '@css-xp/base';
import AkSelect from '@atlaskit/select';
import AkTextfield from '@atlaskit/textfield';
import { colors } from '@atlaskit/theme';
import moment from 'moment';
import Form, { ErrorMessage, Field, FormSection, HelperMessage } from '@atlaskit/form';
import { ErrorMessageOnInfoPanel } from '@css-xp/base/error-page';
import CurrencyField from './currency-field';
import CurrencyTextfield from './currency-textfield';
import { DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { LoadingButton } from '@atlaskit/button';
import './service-credit-form.scss';
import { createCredit } from '@atlassian/temp-proxies';
import { useDispatch } from 'react-redux';
import DOMPurify from 'dompurify';

export interface Props {
  invoice: InvoiceWithLineItems;
}

interface SelectStyleProps extends React.CSSProperties {
  '&:hover': React.CSSProperties;
}

/*
    With the way @atlaskit/form is currently typed, we have to make sure this
    interface is in sync with the Field names
*/
export interface CreditForm {
  invoiceId?: string;
  slaBreachDate?: string;
  amount?: string;
  // @atlaskit/select returns an object instead of the selected `value`
  breachedProduct?: { label: string; value: CreditableProduct };
  supportRequestTicket?: string;
}

type ValidationErrors = { [Field in keyof CreditForm]?: FormValidationErrors };

// @atlaskit/select isn't styled the same way as @atlaskit/textfield
// So we define some overrides here
const getSelectControlStyle = (provided: React.CSSProperties): SelectStyleProps => {
  return {
    ...provided,
    maxWidth: SELECT_WIDTH,
    backgroundColor: colors.N10,
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: colors.N40,
    '&:hover': {
      backgroundColor: colors.N30,
      borderColor: colors.N40,
    },
  };
};

const selectStyleProps = {
  control: getSelectControlStyle,
};

const formatDateDisplayLabel = (date: string, dateFormat: string) => moment.utc(date, dateFormat).format(DATE_FORMAT);

const FIELD_WIDTH = 'xlarge';
const SELECT_WIDTH = '480px';

function ServiceCreditPageWrapper(props: { children: React.ReactNode }) {
  return (
    <div id="service-credit-form">
      <h2 style={{ margin: '20px 0' }}>Request credit</h2>
      {props.children}
    </div>
  );
}

export default function ServiceCreditForm(props: Props) {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const { closeSidebar } = useSidebar();
  const invoice: InvoiceWithLineItems = props.invoice;
  const creditableProducts: Array<NonNullable<CreditForm['breachedProduct']>> = getCreditableProducts(invoice.lineItems)
    .map((lineItem) => ({
      label: lineItem.description || `${lineItem.productKey} (no description provided)`,
      value: lineItem,
    }))
    .sort((a, b) => (a.label < b.label ? -1 : 1));

  const defaultCreditableProductsValue: CreditForm['breachedProduct'] | undefined =
    creditableProducts && creditableProducts.length === 1 ? creditableProducts[0] : undefined;

  const submitCreditRequest = async (creditRequest: ServiceCreditRequest) => {
    setSubmissionError(null);
    setIsSubmitting(true);
    try {
      const credit = await createCredit(creditRequest);
      const notification = successNotificationBuilder()
        .withHeader('Credit request submitted')
        .withId('update-invoice-success')
        .withMessage(
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(`See the <a  target="_blank"
              rel="noopener"
              href="/cloud-sites/${encodeURIComponent(
                credit.cloudSiteId
              )}#credits">credits available</a> for this Cloud site.`),
            }}
          />
        )
        .withDismissAfter(5000)
        .build();
      dispatch(addNotification(notification));
      closeSidebar();
    } catch (error: any) {
      setSubmissionError(error.toString());
    }
    setIsSubmitting(false);
    return;
  };

  const handleFormSubmit = (data: CreditForm): void | ValidationErrors => {
    const {
      invoice: { id: invoiceId },
    } = props;

    const { slaBreachDate, breachedProduct, amount, supportRequestTicket } = data;

    if (!slaBreachDate || !amount || !breachedProduct || !supportRequestTicket) {
      const errors: ValidationErrors = {};

      if (!breachedProduct) {
        errors.breachedProduct = FormValidationErrors.PRODUCT_KEY_MISSING;
      }

      if (!slaBreachDate) {
        errors.slaBreachDate = FormValidationErrors.SLA_BREACH_DATE_MISSING;
      }

      if (!amount) {
        errors.amount = FormValidationErrors.CREDIT_AMOUNT_MISSING;
      }

      if (!supportRequestTicket) {
        errors.supportRequestTicket = FormValidationErrors.SUPPORT_TICKET_INVALID;
      }

      return errors;
    }

    submitCreditRequest({
      slaBreachDate,
      productKey: breachedProduct.value.productKey,
      amount: Number(amount),
      invoiceId,
      supportRequestTicket,
    });
  };

  if (creditableProducts && creditableProducts.length === 0) {
    return (
      <ServiceCreditPageWrapper>
        <InfoPanel
          type="warning"
          title="Unable to create credits for this invoice"
          message={<p>There are no creditable products on this invoice.</p>}
        />
      </ServiceCreditPageWrapper>
    );
  }

  return (
    <ServiceCreditPageWrapper>
      <RequiredFieldMessage />
      <Form onSubmit={handleFormSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormSection>
              <Field label="Invoice number" name="invoiceId" isDisabled defaultValue={invoice.id}>
                {({ fieldProps }) => <AkTextfield {...fieldProps} width={FIELD_WIDTH} />}
              </Field>
            </FormSection>

            <FormSection>
              <Field
                label="Product"
                name="breachedProduct"
                validate={(input: CreditForm['breachedProduct']) => {
                  if (!input || !input.value) {
                    return FormValidationErrors.PRODUCT_KEY_MISSING;
                  }
                }}
                isRequired
                defaultValue={defaultCreditableProductsValue}
              >
                {({ fieldProps: { value: selectedProduct, ...fieldProps }, error, meta: { valid } }) => {
                  return (
                    <>
                      {/* @ts-ignore */}
                      <AkSelect
                        {...fieldProps}
                        value={selectedProduct}
                        styles={selectStyleProps}
                        options={creditableProducts}
                        placeholder="Select the product that breached SLA"
                      />
                      {error && <ErrorMessage>{error}</ErrorMessage>}
                      {valid && (
                        <HelperMessage>
                          Cost of selected product:{' '}
                          <FormattedCurrency
                            currency={invoice.paymentCurrency}
                            value={selectedProduct?.value?.amount ?? 0}
                            showCurrencyCode
                          />
                        </HelperMessage>
                      )}
                    </>
                  );
                }}
              </Field>
            </FormSection>

            <FormSection>
              <Field
                label="SLA ticket number"
                name="supportRequestTicket"
                isRequired
                validate={(input?: CreditForm['supportRequestTicket']) => {
                  if (!input || !isSupportTicket(input)) {
                    return FormValidationErrors.SUPPORT_TICKET_INVALID;
                  }
                }}
              >
                {({ fieldProps, error, meta: { touched } }) => (
                  <>
                    <AkTextfield
                      {...fieldProps}
                      testId="sla-credit-number-text"
                      // Overriding AK's default handling of invalid fields to avoid red border
                      isInvalid={false}
                      width={FIELD_WIDTH}
                      placeholder={"Provide the customer's support ticket reference number"}
                    />
                    {error && touched && <ErrorMessage>{error}</ErrorMessage>}
                  </>
                )}
              </Field>
            </FormSection>

            <FormSection>
              <Field
                label="SLA breach month"
                name="slaBreachDate"
                defaultValue={moment.utc().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')}
                validate={(input?: CreditForm['slaBreachDate']) => {
                  if (!input) {
                    return FormValidationErrors.SLA_BREACH_DATE_MISSING;
                  }

                  if (isFutureDate(input)) {
                    return FormValidationErrors.INVALID_DATE_FROM_FUTURE;
                  }
                }}
                isRequired
              >
                {({ fieldProps, error }) => (
                  <>
                    <AkDatePicker
                      {...fieldProps}
                      placeholder="e.g., 05 Jan 2019"
                      formatDisplayLabel={formatDateDisplayLabel}
                      testId="sla-breach-month"
                      innerProps={{
                        // Hacky way to stop date picker from opening when clicking whitespace next to input
                        style: {
                          maxWidth: SELECT_WIDTH,
                        },
                      }}
                      selectProps={{
                        styles: selectStyleProps,
                      }}
                    />
                    {error && <ErrorMessage>{error}</ErrorMessage>}
                  </>
                )}
              </Field>
            </FormSection>

            <FormSection>
              <CurrencyField
                label="Credit amount"
                name="amount"
                isRequired
                currency={invoice.paymentCurrency}
                // @ts-ignore
                validate={(input: CreditForm['amount'], formState: CreditForm) => {
                  const creditAmount = input ? Number(input) : undefined;

                  const selectedProductCost = formState.breachedProduct
                    ? Number(formState.breachedProduct.value.amount)
                    : undefined;

                  return validateCreditAmount(creditAmount, selectedProductCost);
                }}
              >
                {({ fieldProps, error }) => (
                  <>
                    <CurrencyTextfield
                      {...fieldProps}
                      // Overriding AK's default handling of invalid fields to avoid red border
                      testId="credit-amount"
                      isInvalid={false}
                      width={FIELD_WIDTH}
                      autoComplete="off"
                      currency={invoice.paymentCurrency}
                    />
                    {error && <ErrorMessage>{error}</ErrorMessage>}
                  </>
                )}
              </CurrencyField>
            </FormSection>

            <FormSection>
              <LoadingButton appearance="primary" type="submit" isLoading={isSubmitting}>
                Submit credit request
              </LoadingButton>
            </FormSection>

            {submissionError && (
              <FormSection>
                <InfoPanel
                  type="error"
                  message={
                    <ErrorMessageOnInfoPanel
                      title="We're unable to process the credit request for the following reason"
                      message={submissionError}
                    />
                  }
                />
              </FormSection>
            )}
          </form>
        )}
      </Form>
    </ServiceCreditPageWrapper>
  );
}
